/**
 * **把解析好的主题落到 DOM 上** —— 通道 ①②③ 的落点。
 *
 * ## 三件事
 *
 * ```text
 *   ① tokens  →  内联 CSS 变量（写在 <html> 与设计器根节点两处）
 *   ② recipes →  data-np-<slot> 属性（CSS 用它选配方）
 *   ③ css     →  <style data-np-theme-css> 里（先做作用域变换）
 * ```
 *
 * ## ⚠️ 为什么变量要写**两个**元素，一个不够
 *
 * `theme.css` 把一整块令牌声明在 `.np-designer, .np-node` 上（有它自己的理由：
 * 库被集成进别人的项目时不想污染全局）。而 **CSS 自定义属性是继承的** ——
 * 一个元素**自己声明的**变量会盖掉从祖先继承来的值，跟选择器特异度无关。
 *
 * 所以只往 `<html>` 上写内联变量，设计器根节点会用**它自己样式表里的那份**
 * 盖住，主题**静默失效**。反过来只写设计器根节点，Teleport 到 `body` 的
 * 菜单 / 拾色器又继承不到（`theme.css` 头部记着这个坑）。
 *
 * 结论：**两处都写**。这不是冗余，是这两条约束唯一的交点。
 *
 * ## ⚠️ 可回滚
 *
 * 每次应用都会记下被覆盖的旧值，`dispose()` 时原样还回去。
 * 不记的话「退出设计器」会给宿主页面留下一堆内联变量 ——
 * 而 `index.ts` 明确说这个包**要被集成进别人的 Vue 项目**。
 */
import type { RecipeSlot, ResolvedTheme } from '../types/theme'
import { invalidateThemeRuntime } from './runtime'
import { invalidateTokenNames } from './tokenNames'
import { scopeCss, themeScopeSelector } from './scopeCss'

/** 主题 CSS 的 `<style>` 标签上挂的标记，方便在 devtools 里认出来 */
export const THEME_STYLE_ATTR = 'data-np-theme-css'

/** 主题作用域属性名（`scopeCss` 的选择器就认它） */
export const THEME_ATTR = 'data-np-theme'

/** 配方属性的前缀：槽位 `node` → `data-np-node` */
export function recipeAttr(slot: RecipeSlot): string {
  return `data-np-${slot}`
}

export interface ThemeTargets {
  /** 文档根。令牌写这里，Teleport 到 body 的弹层才继承得到 */
  root: HTMLElement
  /**
   * 设计器根节点（带 `.np-designer` 的那个）。
   * 不传就只写文档根 —— 但那样会被 `.np-designer` 自己的声明盖住。
   */
  scope?: HTMLElement | null
}

/** 一次主题应用的句柄 */
export interface AppliedTheme {
  id: string
  /** 还原本应用改过的一切；重复调用是安全的 */
  dispose: () => void
}

interface Application {
  id: string
  /** 每个元素上被我们覆盖过的属性 → 原值（空串表示原本没有内联值） */
  prev: Map<HTMLElement, Map<string, string>>
  /** 被我们写过、但可能不在这份主题里的旧键 */
  keys: Set<string>
  prevAttrs: Map<HTMLElement, Map<string, string | null>>
  attrNames: Set<string>
  prevColorScheme: string
  styleEl: HTMLStyleElement | null
  targets: ThemeTargets
}

let active: Application | null = null

/** 当前生效的主题 id；没有就是 `null` */
export function activeThemeId(): string | null {
  return active?.id ?? null
}

/**
 * 只生成 CSS 文本，**不碰 DOM** —— 给验证脚本与 SSR 用。
 *
 * 作用域是 `html[data-np-theme="<id>"]`，与 `applyTheme` 写下的属性对应。
 */
export function buildThemeCss(theme: ResolvedTheme): string {
  if (!theme.css.trim()) return ''
  const scoped = scopeCss(theme.css, themeScopeSelector(theme.id))
  return `/* 主题 ${theme.id} —— 作用域由 scopeCss 自动加 */\n${scoped}`
}

/**
 * 应用一份主题。
 *
 * 重复调用会**先撤销上一份**，所以不会累积垃圾内联样式。
 */
export function applyTheme(theme: ResolvedTheme, targets: ThemeTargets): AppliedTheme {
  disposeTheme()

  const app: Application = {
    id: theme.id,
    prev: new Map(),
    keys: new Set(),
    prevAttrs: new Map(),
    attrNames: new Set(),
    prevColorScheme: '',
    styleEl: null,
    targets,
  }

  const elements = uniqueElements([targets.root, targets.scope ?? null])

  /* ── ① 令牌 ── */
  for (const el of elements) {
    const prevForEl = new Map<string, string>()
    for (const [key, value] of Object.entries(theme.tokens)) {
      prevForEl.set(key, el.style.getPropertyValue(key))
      el.style.setProperty(key, value)
      app.keys.add(key)
    }
    app.prev.set(el, prevForEl)
  }

  /* ── ② 配方 + 作用域标记 ── */
  const attrs: Record<string, string> = {
    [THEME_ATTR]: theme.id,
    'data-np-scheme': theme.scheme,
    /*
     * 通道 ⑥ 动效档位。
     *
     * ⚠️ 走**属性**而不是内联变量 —— `theme.css` 里
     * `:root[data-np-motion='off'] { --np-motion-scale: 0 }`，
     * 而 `@media (prefers-reduced-motion: reduce)` 也作用在同一条属性上。
     * 内联的话它会赢过那条媒体查询，"系统开了减少动效却照动不误"。
     */
    'data-np-motion': theme.layout.motion ?? 'normal',
  }
  for (const [slot, value] of Object.entries(theme.recipes)) {
    if (value) attrs[recipeAttr(slot as RecipeSlot)] = value
  }
  for (const el of elements) {
    const prevForEl = new Map<string, string | null>()
    for (const [name, value] of Object.entries(attrs)) {
      prevForEl.set(name, el.getAttribute(name))
      el.setAttribute(name, value)
      app.attrNames.add(name)
    }
    app.prevAttrs.set(el, prevForEl)
  }

  /* ── color-scheme ──
   *
   * ⚠️ 浅色主题必须是 `only light`。只写 `light` 的话，Android Chrome 的
   * 「强制深色网页」仍会插手，把我们已经画成浅色的部分再反暗一次
   * （真机事故，`theme.css` 里记着）。 */
  app.prevColorScheme = targets.root.style.getPropertyValue('color-scheme')
  targets.root.style.setProperty('color-scheme', theme.scheme === 'light' ? 'only light' : 'dark')

  /* ── ③ 任意 CSS ── */
  if (theme.css.trim() && typeof document !== 'undefined') {
    const el = document.createElement('style')
    el.setAttribute(THEME_STYLE_ATTR, theme.id)
    el.textContent = buildThemeCss(theme)
    document.head.appendChild(el)
    app.styleEl = el
  }

  active = app
  /*
   * ⚠️ 通知运行时读回。忘掉这一步的症状是「换了主题，但连线/小地图
   * 还是旧颜色」—— 也就是通道 ④ 本来要消灭的那个 bug。放在这里
   * 而不是让调用方记得调，是因为「靠人记得」已经失败过一次了。
   */
  invalidateThemeRuntime()
  invalidateTokenNames()
  return { id: theme.id, dispose: disposeTheme }
}

/** 撤销当前主题。没有生效中的主题时什么都不做 */
export function disposeTheme(): void {
  const app = active
  if (!app) return
  active = null

  for (const [el, prevForEl] of app.prev) {
    for (const [key, old] of prevForEl) {
      if (old) el.style.setProperty(key, old)
      else el.style.removeProperty(key)
    }
  }
  for (const [el, prevForEl] of app.prevAttrs) {
    for (const [name, old] of prevForEl) {
      if (old === null) el.removeAttribute(name)
      else el.setAttribute(name, old)
    }
  }

  const { root } = app.targets
  if (app.prevColorScheme) root.style.setProperty('color-scheme', app.prevColorScheme)
  else root.style.removeProperty('color-scheme')

  app.styleEl?.remove()
}

/** 去掉重复元素与 null；顺序保持稳定（文档根在前） */
function uniqueElements(list: (HTMLElement | null)[]): HTMLElement[] {
  const out: HTMLElement[] = []
  for (const el of list) {
    if (el && !out.includes(el)) out.push(el)
  }
  return out
}
